import React, {useEffect, useState} from 'react';
import {
    Grid,
    ListItem,
    ListItemIcon,
    ListItemText,
    Checkbox,
    Typography,
} from '@material-ui/core';
import {
    MyAccordion,
    MyAccordionSummary,
    MyAccordionDetails,
} from '../Accordions';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import {makeStyles} from '@material-ui/core/styles';
import {H6} from 'components/TypographyShortcuts';
import InfoModal from 'components/widgets/tooltips/InfoModal';
import {usePutApplicationChecklist} from 'hooks';
import sectionContent from 'components/widgets/checklists/ChecklistSectionContent';
import moment from 'moment';

const useStyles = makeStyles(theme => ({
    root: {
        [theme.breakpoints.down('sm')]: {
            width: '20em',
            height: '15em',
        },
        [theme.breakpoints.up('md')]: {
            width: '40em',
            height: '30em',
        },
    },
    accordion: {
        justifyContent: 'stretch',
        background: theme.palette.gray.light,
        '&.complete': {
            background: theme.palette.type === 'dark' ? theme.palette.success.dark : theme.palette.success.light,
        }
    },
    summary: {
        background: theme.palette.gray.light,
        '&.complete': {
            background: theme.palette.type === 'dark' ? theme.palette.success.dark : theme.palette.success.light,
        }
    },
    checkbox: {
        color: theme.palette.primary.main,
    }
}));

const ChecklistSectionMedical = ({
    sectionId,
    checkoffs,
    complete,
    setComplete,
    mmld,
}) => {
    const classes = useStyles();

    const {put: putApplication, result} = usePutApplicationChecklist();

    const {
        labelId,
        title,
        description,
        secondaryDescription,

        fields,
        infoModalContent,
    } = sectionContent?.[sectionId];

    const pruneData = sourceOfTruth =>
        fields.reduce((object, item) => {
            return {
                ...object,
                [item]: sourceOfTruth?.application_checklist[item] || false,
            };
        }, {});
    const sourceOfTruth = result || checkoffs;
    const [prunedData, setPrunedData] = useState(pruneData(sourceOfTruth));

    const handleChange = value => {
        const newData = {
            ...prunedData,
            [value]: !prunedData[value],
        };
        setPrunedData(newData);
        putApplication(newData);
    };

    useEffect(() => {
        if (result) {
            const thisSectionComplete =
                result?.application_checklist?.[labelId];
            setComplete(thisSectionComplete);
        } else {
            const thisSectionComplete =
                checkoffs?.application_checklist?.[labelId];
            setComplete(thisSectionComplete);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checkoffs, result]);

    // Handle the POST updates in case a checkbox isn't allowed to be checked
    useEffect(() => {
        if (result || checkoffs) {
            setPrunedData(pruneData(sourceOfTruth));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checkoffs, result, sourceOfTruth]);

    //Medical Dates Logic
    if (
        mmld?.recent_mmld_meta.filter(
            docType => docType.DOCUMENT_TYPE === 'MEDICAL CERTIFICATE'
        ).length > 0
    ) {
        const nationalExpireDate =
            mmld?.recent_mmld_meta
                .filter(
                    docType => docType.DOCUMENT_TYPE === 'MEDICAL CERTIFICATE'
                )
                .map(expireDate => expireDate.DOCUMENT_EXPIRE_DT) || {};

        const stcwExpireDate =
            mmld?.recent_mmld_meta
                .filter(
                    docType => docType.DOCUMENT_TYPE === 'MEDICAL CERTIFICATE'
                )
                .map(expireDate => expireDate.MC_STCW_EXPIRE_DT) || {};

        const isNationalExpired = moment(
            nationalExpireDate,
            'YYYY-MM-DDT...'
        ).isBefore();

        const isSTCWExpired = moment(
            stcwExpireDate,
            'YYYY-MM-DDT...'
        ).isBefore();

        return (
            <MyAccordion className={`${classes.accordion} ${complete ? 'complete' : ''}`}>
                <MyAccordionSummary
                    className={`${classes.summary} ${complete ? 'complete' : ''}`}
                    expandIcon={<ExpandMoreIcon />}
                >
                    <Grid container className={classes.titleContainer}>
                        <Grid
                            item
                            style={{
                                display: 'flex',
                                justifyContent: 'left',
                            }}
                            xs={12}
                        >
                            {title ? (
                                <H6>
                                    <strong>{title}</strong>
                                </H6>
                            ) : null}
                            <InfoModal content={infoModalContent} />
                        </Grid>
                    </Grid>
                </MyAccordionSummary>
                <MyAccordionDetails className={`${classes.summary} ${complete ? 'complete' : ''}`}>
                    <ListItem key={labelId} role='button' dense button>
                        <ListItemIcon
                            onClick={() => {
                                handleChange(labelId);
                            }}
                            style={{alignSelf: 'flex-start'}}
                        >
                            <Checkbox
                                className={classes.checkbox}
                                edge='start'
                                checked={prunedData[labelId]}
                                disableRipple
                                inputProps={{
                                    'aria-labelledby': labelId,
                                }}
                            />
                        </ListItemIcon>

                        <Grid container direction='column'>
                            {!isNationalExpired && !isSTCWExpired ? (
                                <Grid item>
                                    <Typography
                                        variant='body2'
                                        style={{
                                            marginTop: '1rem',
                                            marginBottom: '1rem',
                                        }}
                                    >
                                        <strong>
                                            Your Medical Certificate is up to
                                            date.
                                        </strong>
                                    </Typography>

                                    <Typography
                                        variant='body2'
                                        style={{
                                            marginTop: '1rem',
                                            marginBottom: '1rem',
                                        }}
                                    >
                                        Your National Medical is valid until{' '}
                                        {moment(
                                            nationalExpireDate,
                                            'YYYY-MM-DDT...'
                                        ).format('MMM DD, YYYY')}
                                        .
                                    </Typography>
                                    <Typography
                                        variant='body2'
                                        style={{
                                            marginTop: '1rem',
                                            marginBottom: '1rem',
                                        }}
                                    >
                                        Your STCW Medical is valid until{' '}
                                        {moment(
                                            stcwExpireDate,
                                            'YYYY-MM-DDT...'
                                        ).format('MMM DD, YYYY')}
                                        .
                                    </Typography>
                                    <Typography
                                        variant='body2'
                                        style={{
                                            marginTop: '1rem',
                                            marginBottom: '1rem',
                                        }}
                                    >
                                        Check this box to confirm that your
                                        medical will still be up to date for
                                        your application.{' '}
                                    </Typography>
                                    <Typography
                                        variant='body2'
                                        style={{
                                            marginTop: '1rem',
                                            marginBottom: '1rem',
                                        }}
                                    >
                                        OR
                                    </Typography>
                                    <Typography
                                        variant='body2'
                                        style={{
                                            marginTop: '1rem',
                                            marginBottom: '1rem',
                                        }}
                                    >
                                        If you need renew and have submitted a
                                        CG-719K/E Application for Medical
                                        Certificate to the USCG via
                                        MEDAIP@uscg.mil as a pdf
                                    </Typography>
                                </Grid>
                            ) : isSTCWExpired && !isNationalExpired ? (
                                <Grid item>
                                    <Typography
                                        variant='body2'
                                        style={{
                                            marginTop: '1rem',
                                            marginBottom: '1rem',
                                        }}
                                    >
                                        <strong>
                                            Your National Medical Certificate is
                                            up to date, but your STCW Medical
                                            Certificate is expired.
                                        </strong>
                                    </Typography>

                                    <Typography
                                        variant='body2'
                                        style={{
                                            marginTop: '1rem',
                                            marginBottom: '1rem',
                                        }}
                                    >
                                        Your National Medical is valid until{' '}
                                        {moment(
                                            nationalExpireDate,
                                            'YYYY-MM-DDT...'
                                        ).format('MMM DD, YYYY')}
                                        .
                                    </Typography>
                                    <Typography
                                        variant='body2'
                                        style={{
                                            marginTop: '1rem',
                                            marginBottom: '1rem',
                                        }}
                                    >
                                        Your STCW Medical expired on{' '}
                                        {moment(
                                            stcwExpireDate,
                                            'YYYY-MM-DDT...'
                                        ).format('MMM DD, YYYY')}
                                        .
                                    </Typography>
                                    <Typography
                                        variant='body2'
                                        style={{
                                            marginTop: '1rem',
                                            marginBottom: '1rem',
                                        }}
                                    >
                                        Check this box when you have submitted
                                        your CG-719K/E Application for Medical
                                        Certificate to the USCG via
                                        MEDAIP@uscg.mil as a pdf
                                    </Typography>
                                </Grid>
                            ) : isNationalExpired && isSTCWExpired ? (
                                <Grid item>
                                    <Typography
                                        variant='body2'
                                        style={{
                                            marginTop: '1rem',
                                            marginBottom: '1rem',
                                        }}
                                    >
                                        <strong>
                                            Your Medical Certificate is Expired.
                                        </strong>
                                    </Typography>

                                    <Typography
                                        variant='body2'
                                        style={{
                                            marginTop: '1rem',
                                            marginBottom: '1rem',
                                        }}
                                    >
                                        Check this box when you have submitted
                                        your CG-719K/E Application for Medical
                                        Certificate to the USCG via
                                        MEDAIP@uscg.mil as a pdf
                                    </Typography>
                                </Grid>
                            ) : null}
                        </Grid>
                    </ListItem>
                </MyAccordionDetails>
            </MyAccordion>
        );
    } else {
        return (
            <MyAccordion className={`${classes.accordion} ${complete ? 'complete' : ''}`}>
                <MyAccordionSummary
                    className={`${classes.summary} ${complete ? 'complete' : ''}`}
                    expandIcon={<ExpandMoreIcon />}
                >
                    <Grid container className={classes.titleContainer}>
                        <Grid
                            item
                            style={{
                                display: 'flex',
                                justifyContent: 'left',
                            }}
                            xs={12}
                        >
                            {title ? (
                                <H6>
                                    <strong>{title}</strong>
                                </H6>
                            ) : null}
                            <InfoModal content={infoModalContent} />
                        </Grid>
                    </Grid>
                </MyAccordionSummary>
                <MyAccordionDetails className={`${classes.summary} ${complete ? 'complete' : ''}`}>
                    <ListItem key={labelId} role='button' dense button>
                        <ListItemIcon
                            onClick={() => {
                                handleChange(labelId);
                            }}
                            style={{alignSelf: 'flex-start'}}
                        >
                            <Checkbox
                                className={classes.checkbox}
                                edge='start'
                                checked={prunedData[labelId]}
                                disableRipple
                                inputProps={{
                                    'aria-labelledby': labelId,
                                }}
                            />
                        </ListItemIcon>

                        <Grid container direction='column'>
                            {description || secondaryDescription ? (
                                <Grid item>
                                    <ListItemText
                                        //id={labelId}
                                        primary={description}
                                        secondary={secondaryDescription}
                                    />
                                </Grid>
                            ) : null}
                        </Grid>
                    </ListItem>
                </MyAccordionDetails>
            </MyAccordion>
        );
    }
};
export default ChecklistSectionMedical;
